/* ============================================================================
 * accountData.js — account lifecycle (cancel / read-only / reactivate).
 *
 * Backed by migration 110 (sql/110_account_lifecycle_phase3.sql). Every rule
 * is enforced in the database: these are thin wrappers over the owner RPCs,
 * and the screens only explain what the database already allows.
 *
 *   account_state()               any member: where the account stands
 *   account_cancel(keep_profile)  owner: cancel (read-only after paid time)
 *   account_set_keep_profile(b)   owner: change the PQ-profile choice
 *   account_reactivate()          owner: back within the 90 days
 *   account_delete_now('DELETE')  owner: delete everything now (the screen
 *                                 offers the export first)
 *   account_export_request()      owner: build a full export (migration 111)
 *   account_export_state()        owner: the latest export and its links
 *
 * Terms: docs/data-ownership-terms.md §8, §9, §10, §11.
 * ========================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

const ACTIVE = { ok: true, status: 'active', writable: true, is_owner: false };

// Before migration 110 is applied the RPC doesn't exist: treat as active.
const rpcMissing = (error) =>
  !!error &&
  (error.code === 'PGRST202' ||
    error.code === '42883' ||
    /could not find the function|does not exist/i.test(error.message || ''));

export const fetchAccountState = async () => {
  const { data, error } = await supabase.rpc('account_state');
  if (error) {
    if (rpcMissing(error)) return ACTIVE;
    throw error;
  }
  return data?.ok ? data : ACTIVE;
};

const call = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  if (data && data.ok === false) {
    const err = new Error(data.error || 'request_failed');
    err.code = data.error;
    throw err;
  }
  return data;
};

export const cancelAccount = (keepPqProfile = false) =>
  call('account_cancel', { p_keep_pq_profile: !!keepPqProfile });

export const setKeepPqProfile = (keep) =>
  call('account_set_keep_profile', { p_keep: !!keep });

export const reactivateAccount = () => call('account_reactivate');

export const deleteAccountNow = () => call('account_delete_now', { p_confirm: 'DELETE' });

/* Shared hook: state + refresh. Cheap (one RPC), so each consumer owns one. */
export const useAccountState = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setState(await fetchAccountState());
    } catch {
      setState(ACTIVE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { state, loading, refresh, setState };
};

export const isCancelling = (s) =>
  !!s && s.status === 'active' && !!s.cancel_requested_at;

/* ----------------------------------------------------------------------------
 * Full export (migration 111, terms §9): the owner asks, the account-export
 * edge function builds it in the background and emails 7-day links. The
 * latest export (status, links while they last) comes from account_export_state.
 * ------------------------------------------------------------------------- */
export const fetchExportState = async () => {
  const { data, error } = await supabase.rpc('account_export_state');
  if (error) {
    if (rpcMissing(error)) return null;
    throw error;
  }
  return data?.ok ? data.export : null;
};

export const requestExport = async () => {
  const data = await call('account_export_request');
  return data?.export ?? null;
};

export const exportBusy = (e) => !!e && (e.status === 'queued' || e.status === 'working');

/* Latest export; polls every 5 s while one is being built. */
export const useExportState = (enabled = true) => {
  const [exp, setExp] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setExp(await fetchExportState());
    } catch {
      /* keep what we have */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !exportBusy(exp)) return undefined;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [enabled, exp, refresh]);

  return { exp, setExp, loaded, refresh };
};
